import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Edit, DollarSign, ChevronDown, ChevronUp, Save, X } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

const RateListManagement = () => {
  const [rateLists, setRateLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRateList, setEditingRateList] = useState(null);
  const [expandedList, setExpandedList] = useState(null);
  const [editingEntryIndex, setEditingEntryIndex] = useState(null);
  const [editingEntryData, setEditingEntryData] = useState(null);
  
  // Form state for rate list
  const [formData, setFormData] = useState({
    name: '',
    entries: [],
  });
  
  // Form state for adding new entry
  const [newEntry, setNewEntry] = useState({
    circumference_from: '',
    circumference_to: '',
    rate: '',
  });

  useEffect(() => {
    fetchRateLists();
  }, []);

  const fetchRateLists = async () => {
    try {
      const response = await apiClient.get('/rate-lists');
      setRateLists(response.data);
    } catch (error) {
      toast.error('Failed to fetch rate lists');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a rate list name');
      return;
    }

    try {
      if (editingRateList) {
        await apiClient.put(`/rate-lists/${editingRateList.id}`, formData);
        toast.success('Rate list updated successfully');
      } else {
        await apiClient.post('/rate-lists', formData);
        toast.success('Rate list created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchRateLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rate list');
    }
  };

  const handleEdit = (rateList) => {
    setEditingRateList(rateList);
    setFormData({
      name: rateList.name,
      entries: rateList.entries || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (rateListId) => {
    if (!window.confirm('Are you sure you want to delete this rate list?')) return;

    try {
      await apiClient.delete(`/rate-lists/${rateListId}`);
      toast.success('Rate list deleted successfully');
      fetchRateLists();
    } catch (error) {
      toast.error('Failed to delete rate list');
    }
  };

  const resetForm = () => {
    setEditingRateList(null);
    setFormData({
      name: '',
      entries: [],
    });
    setNewEntry({
      circumference_from: '',
      circumference_to: '',
      rate: '',
    });
  };

  const addEntry = () => {
    const from = parseFloat(newEntry.circumference_from);
    const to = parseFloat(newEntry.circumference_to);
    const rate = parseFloat(newEntry.rate);

    if (isNaN(from) || isNaN(to) || isNaN(rate)) {
      toast.error('Please fill all fields with valid numbers');
      return;
    }

    if (from >= to) {
      toast.error('Circumference "From" must be less than "To"');
      return;
    }

    setFormData({
      ...formData,
      entries: [...formData.entries, { circumference_from: from, circumference_to: to, rate }],
    });
    setNewEntry({ circumference_from: '', circumference_to: '', rate: '' });
  };

  const removeEntry = (index) => {
    const newEntries = formData.entries.filter((_, i) => i !== index);
    setFormData({ ...formData, entries: newEntries });
  };

  const toggleExpand = (listId) => {
    setExpandedList(expandedList === listId ? null : listId);
    setEditingEntryIndex(null);
    setEditingEntryData(null);
  };

  const startEditEntry = (rateListId, index, entry) => {
    setEditingEntryIndex({ listId: rateListId, index });
    setEditingEntryData({ ...entry });
  };

  const cancelEditEntry = () => {
    setEditingEntryIndex(null);
    setEditingEntryData(null);
  };

  const saveEditEntry = async (rateList) => {
    if (!editingEntryData || editingEntryIndex === null) return;
    
    const from = parseFloat(editingEntryData.circumference_from);
    const to = parseFloat(editingEntryData.circumference_to);
    const rate = parseFloat(editingEntryData.rate);

    if (isNaN(from) || isNaN(to) || isNaN(rate)) {
      toast.error('Please fill all fields with valid numbers');
      return;
    }

    if (from >= to) {
      toast.error('Circumference "From" must be less than "To"');
      return;
    }

    try {
      const updatedEntries = [...rateList.entries];
      updatedEntries[editingEntryIndex.index] = {
        circumference_from: from,
        circumference_to: to,
        rate
      };

      await apiClient.put(`/rate-lists/${rateList.id}`, {
        name: rateList.name,
        entries: updatedEntries
      });
      
      toast.success('Rate entry updated successfully');
      setEditingEntryIndex(null);
      setEditingEntryData(null);
      fetchRateLists();
    } catch (error) {
      toast.error('Failed to update rate entry');
    }
  };

  const deleteRateEntry = async (rateList, entryIndex) => {
    if (!window.confirm('Are you sure you want to delete this rate entry?')) return;

    try {
      const updatedEntries = rateList.entries.filter((_, i) => i !== entryIndex);
      await apiClient.put(`/rate-lists/${rateList.id}`, {
        name: rateList.name,
        entries: updatedEntries
      });
      toast.success('Rate entry deleted');
      fetchRateLists();
    } catch (error) {
      toast.error('Failed to delete rate entry');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6 text-slate-700" />
          <h2 className="text-2xl font-primary font-semibold text-slate-900">Rate List Management</h2>
        </div>
        <Button
          data-testid="add-rate-list-button"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Add Rate List
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-600">Loading rate lists...</div>
      ) : rateLists.length === 0 ? (
        <Card className="p-8 text-center">
          <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No rate lists found. Create your first rate list above.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {rateLists.map((rateList) => (
            <Card key={rateList.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              {/* Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={() => toggleExpand(rateList.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedList === rateList.id ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-900">{rateList.name}</h3>
                    <p className="text-sm text-slate-500">
                      {rateList.entries?.length || 0} rate entries
                    </p>
                  </div>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    data-testid={`edit-rate-list-${rateList.id}`}
                    onClick={() => handleEdit(rateList)}
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid={`delete-rate-list-${rateList.id}`}
                    onClick={() => handleDelete(rateList.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Expanded Content */}
              {expandedList === rateList.id && (
                <div className="border-t border-slate-200 p-4">
                  {rateList.entries?.length > 0 ? (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th colSpan={2} className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4 border-r border-slate-200">
                            Circumference (mm)
                          </th>
                          <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">
                            Rate (₹)
                          </th>
                          <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4 w-24">
                            Actions
                          </th>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <th className="text-center text-xs font-medium text-slate-400 py-1 px-4">From</th>
                          <th className="text-center text-xs font-medium text-slate-400 py-1 px-4 border-r border-slate-200">To</th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rateList.entries.map((entry, index) => {
                          const isEditing = editingEntryIndex?.listId === rateList.id && editingEntryIndex?.index === index;
                          
                          return (
                            <tr key={index} className="border-t border-slate-100">
                              {isEditing ? (
                                <>
                                  <td className="py-2 px-2">
                                    <Input
                                      type="number"
                                      value={editingEntryData?.circumference_from || ''}
                                      onChange={(e) => setEditingEntryData({...editingEntryData, circumference_from: e.target.value})}
                                      className="h-8 text-center text-sm"
                                    />
                                  </td>
                                  <td className="py-2 px-2 border-r border-slate-200">
                                    <Input
                                      type="number"
                                      value={editingEntryData?.circumference_to || ''}
                                      onChange={(e) => setEditingEntryData({...editingEntryData, circumference_to: e.target.value})}
                                      className="h-8 text-center text-sm"
                                    />
                                  </td>
                                  <td className="py-2 px-2">
                                    <Input
                                      type="number"
                                      value={editingEntryData?.rate || ''}
                                      onChange={(e) => setEditingEntryData({...editingEntryData, rate: e.target.value})}
                                      className="h-8 text-center text-sm"
                                    />
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => saveEditEntry(rateList)}
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50 h-7 w-7 p-0"
                                      >
                                        <Save className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={cancelEditEntry}
                                        className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-7 w-7 p-0"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="py-2 px-4 text-center text-sm text-slate-900">{entry.circumference_from}</td>
                                  <td className="py-2 px-4 text-center text-sm text-slate-900 border-r border-slate-200">{entry.circumference_to}</td>
                                  <td className="py-2 px-4 text-center text-sm font-bold text-slate-900">₹{entry.rate?.toLocaleString()}</td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => startEditEntry(rateList.id, index, entry)}
                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deleteRateEntry(rateList, index)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No rate entries. Click edit to add entries.</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="rate-list-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-primary font-semibold">
              {editingRateList ? 'Edit Rate List' : 'Create New Rate List'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label>Rate List Name *</Label>
              <Input
                data-testid="rate-list-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard Rate, Premium Rate"
                required
                className="mt-2 h-12"
              />
            </div>

            {/* Entries Table */}
            <div>
              <Label className="mb-3 block">Rate Entries</Label>
              
              {formData.entries.length > 0 && (
                <div className="border rounded-lg overflow-hidden mb-4">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3">From (mm)</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3">To (mm)</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3">Rate (₹)</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3 w-16">Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.entries.map((entry, index) => (
                        <tr key={index} className="border-t border-slate-100">
                          <td className="py-2 px-3 text-center text-sm">{entry.circumference_from}</td>
                          <td className="py-2 px-3 text-center text-sm">{entry.circumference_to}</td>
                          <td className="py-2 px-3 text-center text-sm font-medium">₹{entry.rate?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeEntry(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add New Entry Form */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-3">Add Rate Entry</p>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">From (mm)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newEntry.circumference_from}
                      onChange={(e) => setNewEntry({ ...newEntry, circumference_from: e.target.value })}
                      placeholder="100"
                      className="mt-1 h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">To (mm)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newEntry.circumference_to}
                      onChange={(e) => setNewEntry({ ...newEntry, circumference_to: e.target.value })}
                      placeholder="200"
                      className="mt-1 h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Rate (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newEntry.rate}
                      onChange={(e) => setNewEntry({ ...newEntry, rate: e.target.value })}
                      placeholder="5000"
                      className="mt-1 h-10"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addEntry}
                      className="w-full h-10"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Button
              data-testid="submit-rate-list-button"
              type="submit"
              disabled={!formData.name.trim()}
              className="w-full bg-slate-900 hover:bg-slate-800 h-12"
            >
              {editingRateList ? 'Update Rate List' : 'Create Rate List'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RateListManagement;
